// Custom hook for drawer state and scrollbar management

import { useState, useRef, useEffect } from 'react';
import drawerConfig from '../drawerConfig';

/**
 * useDrawer - Manages drawer state and auto-hiding scrollbar behavior
 * 
 * @returns {Object} - Drawer state and handlers
 * @returns {boolean} open - Whether drawer is expanded
 * @returns {boolean} showScrollbar - Whether scrollbar should be visible
 * @returns {Function} handleDrawer - Toggle drawer open/closed
 * @returns {Function} handleMouseMove - Show scrollbar on mouse movement
 * @returns {Function} handleMouseLeave - Hide scrollbar when mouse leaves
 */
export const useDrawer = () => {
  const [open, setOpen] = useState(drawerConfig.defaultOpen);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const scrollbarTimeoutRef = useRef(null);

  const handleDrawer = (state) => setOpen(state);

  const handleMouseMove = () => {
    // Show scrollbar on mouse movement
    setShowScrollbar(true);
    
    // Clear existing timeout
    if (scrollbarTimeoutRef.current) {
      clearTimeout(scrollbarTimeoutRef.current);
    }
    
    // Hide scrollbar after 1 second of no mouse movement
    scrollbarTimeoutRef.current = setTimeout(() => {
      setShowScrollbar(false);
    }, 1000);
  };

  const handleMouseLeave = () => {
    // Hide immediately when mouse leaves
    if (scrollbarTimeoutRef.current) {
      clearTimeout(scrollbarTimeoutRef.current);
    }
    setShowScrollbar(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollbarTimeoutRef.current) {
        clearTimeout(scrollbarTimeoutRef.current);
      }
    };
  }, []);

  return {
    open,
    showScrollbar,
    handleDrawer,
    handleMouseMove,
    handleMouseLeave,
  };
};

export default useDrawer;
